import { FC, useEffect, useRef } from 'react';

import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { Box, Button, InputAdornment, Stack, Typography } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { useMutation } from '@redwoodjs/web';

import { ForgotPasswordSVG } from 'src/assets/images';
import { FieldInput } from 'src/components/FormikComponents';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { FORGOT_PASSWORD_MUTATION } from 'src/graphql/mutations/login.mutation';

import { ExclamationIcon } from './ExclamationIcon';

const FORGOT_PASSWORD_MESSAGE =
    'If a matching account was found an email has been sent to this address to allow you to reset your password.';

interface ForgotPasswordFormData {
    email: string;
}

const ForgotPasswordFormValidation = Yup.object().shape({
    email: Yup.string().email('Please enter a valid email').required('Email is required'),
});

interface ForgotPasswordProps {
    handleClose: () => void;
}

const ForgotPassword: FC<ForgotPasswordProps> = ({ handleClose }) => {
    const [forgotPassword, { loading }] = useMutation(FORGOT_PASSWORD_MUTATION, {
        onCompleted: () => {
            showSuccessToast(FORGOT_PASSWORD_MESSAGE, <ExclamationIcon />);
            setTimeout(() => handleClose(), 5000);
        },
        onError: () => {
            showErrorToast(FORGOT_PASSWORD_MESSAGE, <ExclamationIcon />);
        },
    });

    const onSubmitForgotPassword = async (
        values: ForgotPasswordFormData,
        { setSubmitting }: FormikHelpers<ForgotPasswordFormData>
    ) => {
        setSubmitting(true);
        await forgotPassword({
            variables: {
                ...values,
            },
        });
    };

    const emailInputRef = useRef(null);

    useEffect(() => {
        const timeout = setTimeout(() => {
            emailInputRef.current.focus();
        }, 100);

        return () => {
            clearTimeout(timeout);
        };
    }, []);

    return (
        <Formik
            initialValues={{ email: '' }}
            onSubmit={onSubmitForgotPassword}
            validationSchema={ForgotPasswordFormValidation}
        >
            {({ isSubmitting, values, errors }) => {
                return (
                    <Form>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                            }}
                        >
                            <Box>
                                <ForgotPasswordSVG />
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    color: '#000',
                                }}
                            >
                                Forgot Password
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    color: '#667085',
                                    marginBottom: '16px',
                                }}
                            >
                                Forgot your password? Enter your email address below and we&apos;ll
                                send you a link to reset it. Follow the instructions in the email to
                                create a new password.
                            </Typography>

                            <FieldInput
                                label="Email"
                                name="email"
                                ref={emailInputRef}
                                textFieldProps={{
                                    InputProps: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailOutlinedIcon />{' '}
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                type="email"
                            />

                            <Stack
                                direction="row"
                                justifyContent="justify-between"
                                spacing={2}
                                sx={{ mt: 3 }}
                            >
                                <Button
                                    variant="outlined-black"
                                    onClick={handleClose}
                                    fullWidth
                                    sx={{ width: '170px', height: '44px' }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="main"
                                    disabled={
                                        isSubmitting ||
                                        values.email.length === 0 ||
                                        Object.values(errors).length > 0
                                    }
                                    fullWidth
                                    sx={{ width: '170px', height: '44px' }}
                                >
                                    {isSubmitting || loading ? 'Please wait...' : 'Confirm'}
                                </Button>
                            </Stack>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default ForgotPassword;
